import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  AccountBalance as PayoutIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  ViewList as ViewListIcon,
  // Payment as PaymentIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AutorenewRounded as AutorenewIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { virtualBankAccountAPI } from '../services/api';
import { Link } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { useSyncContext } from '../contexts/SyncContext';
import FundingEventsTable from '../components/FundingEventsTable';

const VirtualBankAccounts = () => {
  const { syncTrigger, triggerSyncRefresh } = useSyncContext();

  const [accounts, setAccounts] = useState([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingNickname, setEditingNickname] = useState(null);
  const [nicknameValue, setNicknameValue] = useState('');
  const [payoutDialog, setPayoutDialog] = useState({ open: false, accountId: null, amount: '' });
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [simulatePaymentDialog, setSimulatePaymentDialog] = useState({ open: false, accountId: null, amount: '' });
  const [generating, setGenerating] = useState(null); // null, 'single', or 'multiple'
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [fundingEventsDialog, setFundingEventsDialog] = useState({ open: false, customerId: null, customerName: '' });

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Listen for sync triggers from other components (like initial sync in Layout)
  useEffect(() => {
    if (syncTrigger > 0) {
      // Refresh accounts data when a sync trigger is received
      fetchAccounts();
    }
  }, [syncTrigger]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await virtualBankAccountAPI.getAllComprehensive();
      setAccounts(response.data.accounts);
      setTotalBalance(response.data.totalBalance);
      setError('');
    } catch (err) {
      setError('Failed to fetch virtual bank accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateCustomers = async (count) => {
    try {
      setGenerating(count === 1 ? 'single' : 'multiple');
      const promises = [];

      for (let i = 0; i < count; i++) {
        const customerData = {
          firstName: `Customer${Math.floor(Math.random() * 10000)}`,
          lastName: `User${Math.floor(Math.random() * 10000)}`,
          email: `customer${Math.floor(Math.random() * 100000)}@example.com`
          // nickname is intentionally left empty
        };
        promises.push(virtualBankAccountAPI.createCustomerAndAccount(customerData));
      }

      await Promise.all(promises);
      setSuccess(`Successfully generated ${count} customer${count > 1 ? 's' : ''} with virtual bank accounts!`);
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to generate ${count} customer${count > 1 ? 's' : ''}`);
    } finally {
      setGenerating(null);
    }
  };

  const handleUpdateNickname = async (accountId) => {
    try {
      await virtualBankAccountAPI.updateNickname(accountId, nicknameValue);
      setSuccess('Nickname updated successfully!');
      setEditingNickname(null);
      setNicknameValue('');
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update nickname');
    }
  };

  const handlePayout = async () => {
    try {
      const amount = parseFloat(payoutDialog.amount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      setPayoutLoading(true);
      setError('');

      const response = await virtualBankAccountAPI.initiatePayout(payoutDialog.accountId, amount);

      // Display payment intent information
      const paymentIntent = response.data.paymentIntent;
      if (paymentIntent) {
        setSuccess(
          `Payout of $${amount.toFixed(2)} initiated successfully!\n` +
          `Payment Intent ID: ${paymentIntent.id}\n` +
          `Status: ${paymentIntent.status}\n` +
          `Amount: $${(paymentIntent.amount / 100).toFixed(2)}`
        );
      } else {
        setSuccess(`Payout of $${amount.toFixed(2)} initiated successfully!`);
      }

      setPayoutDialog({ open: false, accountId: null, amount: '' });
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate payout');
    } finally {
      setPayoutLoading(false);
    }
  };

  const startEditingNickname = (accountId, currentNickname) => {
    setEditingNickname(accountId);
    setNicknameValue(currentNickname || '');
  };

  const cancelEditingNickname = () => {
    setEditingNickname(null);
    setNicknameValue('');
  };

  const openPayoutDialog = (accountId) => {
    setPayoutDialog({ open: true, accountId, amount: '' });
  };

  const closePayoutDialog = () => {
    setPayoutDialog({ open: false, accountId: null, amount: '' });
    setPayoutLoading(false);
  };

  const handleSimulatePayment = async () => {
    try {
      const amount = parseFloat(simulatePaymentDialog.amount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      await virtualBankAccountAPI.simulatePayment(simulatePaymentDialog.accountId, amount);
      setSuccess(`Payment of $${amount.toFixed(2)} simulated successfully!`);
      setSimulatePaymentDialog({ open: false, accountId: null, amount: '' });
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to simulate payment');
    }
  };

  // const openSimulatePaymentDialog = (accountId) => {
  //   setSimulatePaymentDialog({ open: true, accountId, amount: '' });
  // };

  const closeSimulatePaymentDialog = () => {
    setSimulatePaymentDialog({ open: false, accountId: null, amount: '' });
  };

  const openFundingEventsDialog = (customerId, customerName) => {
    setFundingEventsDialog({ open: true, customerId, customerName });
  };

  const closeFundingEventsDialog = () => {
    setFundingEventsDialog({ open: false, customerId: null, customerName: '' });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const closeSnackbar = () => {
    setError('');
    setSuccess('');
  };

  const syncAllBalances = async () => {
    try {
      setSyncing(true);
      setError('');

      // Call the sync API endpoint
      const response = await virtualBankAccountAPI.syncAll();

      // Trigger sync refresh for other components
      triggerSyncRefresh();

      // Refresh the accounts data to show updated balances
      await fetchAccounts();

      setSuccess(`Successfully synced ${response.data.syncedCount || 'all'} account balances with Stripe!`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sync balances with Stripe');
      console.error('Error syncing balances:', err);
    } finally {
      setSyncing(false);
    }
  };

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      account.accountNumber?.toLowerCase().includes(searchLower) ||
      account.nickname?.toLowerCase().includes(searchLower) ||
      account.stripeCustomerId?.toLowerCase().includes(searchLower) ||
      account.balance?.toString().includes(searchTerm) ||
      account.totalPaid?.toString().includes(searchTerm)
    );
  });

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      // height: '100vh',
      backgroundColor: 'background.default'
    }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Virtual Bank Customers
          </Typography>
          <Box component={Link} sx={{ color: "inherit" }} to={"/all-accounts"}>
            <Button
              color="inherit"
              startIcon={<ViewListIcon />}
              onClick={() => window.location.href = '/all-accounts'}
              sx={{ mr: 2 }}
            >
              All Accounts
            </Button>
          </Box>
          <ThemeToggle />
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ ml: 3 }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        p: 2,
        backgroundColor: 'background.default',
        overflow: 'hidden'
      }}>
        {/* Action Cards */}
        <Grid container spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Typography variant="h6" gutterBottom>
                  Generate Customers
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={
                      generating === 'single' ? (
                        <RefreshIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                      ) : (
                        <AddIcon />
                      )
                    }
                    onClick={() => generateCustomers(1)}
                    disabled={generating !== null}
                    sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                  >
                    {generating === 'single' ? 'Generating...' : 'Generate 1 Bank'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      generating === 'multiple' ? (
                        <AutorenewIcon sx={{ animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                      ) : (
                        <AddIcon />
                      )
                    }
                    onClick={() => generateCustomers(10)}
                    disabled={generating !== null}
                    sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                  >
                    {generating === 'multiple' ? 'Generating 10...' : 'Generate 10 Banks'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Typography variant="h6" gutterBottom>
                  Sync Balances
                </Typography>
                <Button
                  variant="contained"
                  startIcon={
                    syncing ? (
                      <RefreshIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={syncAllBalances}
                  disabled={syncing || generating !== null}
                  sx={{ bgcolor: 'info.main', '&:hover': { bgcolor: 'info.dark' } }}
                  fullWidth
                >
                  {syncing ? 'Syncing...' : 'Sync with Stripe'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ pb: "16px !important" }}>
                <Typography variant="h6" gutterBottom>
                  Grand Total Balance
                </Typography>
                <Typography variant="h4" margin={"0"} color="primary.main" fontWeight="bold">
                  ${totalBalance.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search Bar */}
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search accounts by account number, nickname, customer ID, balance, or total paid..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              }
            }}
          />
        </Box>

        {/* Main Table */}
        <Paper sx={{
          width: '100%',
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <TableContainer sx={{
            flex: 1,
            overflow: 'auto'
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Customer ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Account Holder
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Account Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Routing Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Balance
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Nickname
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Update Nickname
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Payout
                  </TableCell>
                  {/* <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Simulate
                  </TableCell> */}
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Copy Details
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Funding Events
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Total Paid
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading virtual bank accounts...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No virtual bank accounts found. Generate some customers to get started!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No accounts match your search criteria. Try a different search term.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAccounts.map((account) => (
                    <TableRow key={account._id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {account.stripeCustomerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.accountHolderName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {account.accountNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {account.routingNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`$${(account.balance || 0).toFixed(2)}`}
                          color={account.balance > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {editingNickname === account._id ? (
                          <TextField
                            size="small"
                            value={nicknameValue}
                            onChange={(e) => setNicknameValue(e.target.value)}
                            placeholder="Enter nickname"
                            sx={{ minWidth: 150 }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: account.nickname ? 'text.primary' : 'text.secondary',
                              fontStyle: account.nickname ? 'normal' : 'italic'
                            }}
                          >
                            {account.nickname || 'No nickname set'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingNickname === account._id ? (
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUpdateNickname(account._id)}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={cancelEditingNickname}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => startEditingNickname(account._id, account.nickname)}
                          >
                            Update Nickname
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PayoutIcon />}
                          onClick={() => openPayoutDialog(account._id)}
                          disabled={!account.balance || account.balance <= 0}
                          sx={{
                            bgcolor: 'warning.main',
                            '&:hover': { bgcolor: 'warning.dark' },
                            '&.Mui-disabled': {
                              bgcolor: 'grey.400',
                              color: 'grey.600',
                              opacity: 0.6,
                            }
                          }}
                        >
                          Payout
                        </Button>
                      </TableCell>
                      {/* <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PaymentIcon />}
                          onClick={() => openSimulatePaymentDialog(account._id)}
                          sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                        >
                          Payment
                        </Button>
                      </TableCell> */}
                      <TableCell>
                        <Tooltip title="Copy account details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => copyToClipboard(
                              `Account: ${account.accountNumber}, Routing: ${account.routingNumber}, Account Holder: ${account.accountHolderName}`
                            )}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View funding events">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => openFundingEventsDialog(account.stripeCustomerId, account.accountHolderName)}
                          >
                            <TimelineIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`$${(account.totalPaid || 0).toFixed(2)}`}
                          color={account.totalPaid > 0 ? 'info' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Payout Dialog */}
      <Dialog
        open={payoutDialog.open}
        onClose={payoutLoading ? undefined : closePayoutDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            Initiate Payout

          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Payout Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={payoutDialog.amount}
            onChange={(e) => setPayoutDialog({ ...payoutDialog, amount: e.target.value })}
            disabled={payoutLoading}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              step: 0.01,
              min: 0.01
            }}
            sx={{ mt: 2 }}
          />

        </DialogContent>
        <DialogActions>
          <Button
            onClick={closePayoutDialog}
            disabled={payoutLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayout}
            variant="contained"
            disabled={payoutLoading}
            startIcon={payoutLoading ? <CircularProgress size={16} /> : <PayoutIcon />}
          >
            {payoutLoading ? 'Processing...' : 'Initiate Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Simulate Payment Dialog */}
      <Dialog open={simulatePaymentDialog.open} onClose={closeSimulatePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Simulate Payment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Payment Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={simulatePaymentDialog.amount}
            onChange={(e) => setSimulatePaymentDialog({ ...simulatePaymentDialog, amount: e.target.value })}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              step: 0.01,
              min: 0.01
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSimulatePaymentDialog}>Cancel</Button>
          <Button onClick={handleSimulatePayment} variant="contained" color="success">
            Simulate Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={8000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={error ? 'error' : 'success'}
          sx={{
            width: '100%',
            maxWidth: '500px',
            '& .MuiAlert-message': {
              whiteSpace: 'pre-line',
              wordBreak: 'break-word'
            }
          }}
        >
          {error || success}
        </Alert>
      </Snackbar>

      {/* Funding Events Dialog */}
      <Dialog
        open={fundingEventsDialog.open}
        onClose={closeFundingEventsDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Funding Events - {fundingEventsDialog.customerName}
        </DialogTitle>
        <DialogContent>
          {fundingEventsDialog.customerId && (
            <FundingEventsTable customerId={fundingEventsDialog.customerId} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFundingEventsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VirtualBankAccounts;